/**
 * Pill — status in the panel's own vocabulary (§5), not generic
 * success/warning/error. A pill that says "Down" tells you more than one that
 * says "Error".
 *
 *   <Pill state="live" />            → ● Live
 *   <Pill state="down">Exited</Pill> → ● Exited, in the "down" colours
 *
 * Every pill carries a word next to its dot, so colour is never the sole
 * carrier of meaning (§8).
 *
 * The text colours below are the mockup's darkened variants of the state
 * tokens, not the tokens themselves: --color-ok on --color-s-green is too
 * light to read at 11.5px. Same hue, contrast-corrected — do not "fix" these
 * back to the raw token.
 */

const STATES = {
  // Site liveness — the mockup's four.
  live: { label: 'Live', className: 'bg-s-green text-[#046B57]' },
  deploying: { label: 'Deploying', className: 'bg-s-blue text-[#2E5C8A]' },
  suspended: { label: 'Suspended', className: 'bg-[#FAEEDA] text-[#8B5206]' },
  down: { label: 'Down', className: 'bg-err-bg text-err' },

  // Billing — the `sites.subscription_status` column ('active' | 'past_due' |
  // 'suspended'). Kept as its own vocabulary on purpose: a paid-up subscription
  // is not the same claim as a container that answers, and reusing `live` for it
  // would let the Sites table imply a liveness it never measured.
  active: { label: 'Active', className: 'bg-s-green text-[#046B57]' },
  past_due: { label: 'Past due', className: 'bg-warn-bg text-[#8B5206]' },
};

// Unknown state → neutral grey, label falls back to the raw value. Falling back
// to `down` (as this did when `down` was the only sensible default) would let a
// new backend status value render as an outage.
const UNKNOWN = { label: null, className: 'bg-s-3 text-t2' };

export function Pill({ state, children, className = '' }) {
  const spec = STATES[state] ?? UNKNOWN;
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 px-[9px] py-1 rounded-xl',
        'text-[11.5px] font-medium',
        spec.className,
        className,
      ].join(' ')}
    >
      <i
        className="block w-1.5 h-1.5 rounded-full bg-current shrink-0"
        aria-hidden="true"
      />
      {children ?? spec.label ?? state}
    </span>
  );
}
